// Transferências entre contas — lançamento manual + estado de conciliação.
//
// Decisão do Lucas (2026-08-10): primeiro LANÇA-SE a transferência (o ato: o
// PIX, a aplicação, o resgate), depois a conciliação CONFIRMA contra o extrato.
// Mesmo modelo do Conta Azul, cujo sync está pausado — sem isto a tabela ficou
// sem escritor e a conta Investimento ficou cega (R$ 509k em 24 movimentos).
//
// O casamento é automático: o candidato 'transferencia_ca' da conciliação já
// casa OFX contra estes registros por valor+data+banco. Lançou → o extrato
// sugere sozinho quando chegar.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Button, Card, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography,
  App as AntApp, Popconfirm, Flex,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SwapOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr, hojeIso } from '../../utils/dataFmt';
import { getToken, fmt } from './utils';

const { Text } = Typography;

type ContaFin = { id: string; nome: string; tipo: string; ativo?: boolean };
type Transf = {
  id: string; origem: string; descricao: string | null; valor: number; data: string;
  origem_conta_nome: string | null; destino_conta_nome: string | null;
  saida_conciliada: boolean; entrada_conciliada: boolean;
};

const ORIGEM_LABEL: Record<string, string> = {
  manual: 'Manual', conta_azul: 'Conta Azul', extrato: 'Par do extrato', cartao_manual: 'Fatura cartão',
};

export function ModalTransferencias({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const { message } = AntApp.useApp();
  const [lista, setLista] = useState<Transf[]>([]);
  const [contas, setContas] = useState<ContaFin[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    origem_conta_id: '', destino_conta_id: '', valor: null as number | null,
    data: hojeIso(), descricao: '',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const token = await getToken();
      const [rT, rC] = await Promise.all([
        axios.get(`${API_URL}/financeiro/transferencias`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/financeiro/contas`, { headers: { Authorization: token } }),
      ]);
      const dT = rT.data?.body ? JSON.parse(rT.data.body) : rT.data;
      const dC = rC.data?.body ? JSON.parse(rC.data.body) : rC.data;
      setLista(dT.transferencias || []);
      setContas((dC.contas || dC || []).filter((c: ContaFin) => c.ativo !== false));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }, [message]);

  useEffect(() => { if (aberto) carregar(); }, [aberto, carregar]);

  async function lancar(forcar = false) {
    if (!form.origem_conta_id || !form.destino_conta_id || !form.valor) {
      message.warning('Informe origem, destino e valor.'); return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/transferencias`,
        { ...form, forcar: forcar || undefined },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      message.success(d.aviso || 'Transferência lançada.');
      setForm(f => ({ ...f, valor: null, descricao: '' }));
      await carregar();
    } catch (e: any) {
      const d = e.response?.data?.body ? JSON.parse(e.response.data.body) : e.response?.data;
      // 409 = suspeita de duplicata: mostra o que já existe e deixa forçar —
      // duas aplicações iguais na mesma semana existem de verdade.
      if (e.response?.status === 409 && d?.pode_forcar) {
        Modal.confirm({
          title: 'Já existe transferência parecida',
          content: (
            <div>
              {(d.suspeitas || []).map((s: any) => (
                <div key={s.id} style={{ fontSize: 12, marginBottom: 4 }}>
                  • {fmtDataBr(s.data)} · {fmt(Math.abs(s.valor))} · {s.origem_conta_nome} → {s.destino_conta_nome}
                  <Tag style={{ marginLeft: 6, fontSize: 10 }}>{ORIGEM_LABEL[s.origem] || s.origem}</Tag>
                </div>
              ))}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Se for outra transferência de mesmo valor (ex.: duas aplicações na semana), pode lançar mesmo assim.
              </Text>
            </div>
          ),
          okText: 'Lançar mesmo assim',
          onOk: () => lancar(true),
        });
      } else {
        message.error(d?.error || e.message);
      }
    } finally { setSalvando(false); }
  }

  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/financeiro/transferencias/${id}`, { headers: { Authorization: token } });
      message.success('Transferência excluída.');
      await carregar();
    } catch (e: any) {
      const d = e.response?.data?.body ? JSON.parse(e.response.data.body) : e.response?.data;
      message.error(d?.error || e.message);
    }
  }

  const contasOpts = contas.map(c => ({ value: c.id, label: `${c.nome}${c.tipo ? ` (${c.tipo})` : ''}` }));

  return (
    <Modal
      title={<Space><SwapOutlined />Transferências entre contas</Space>}
      open={aberto} onCancel={onFechar} footer={null}
      width="94vw" style={{ maxWidth: 960, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
    >
      <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }}>
        <Text strong style={{ fontSize: 13 }}>Lançar transferência</Text>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          Registre o ato (PIX entre contas, aplicação, resgate). Quando o extrato chegar, a
          conciliação sugere o casamento sozinha — e a conta sem extrato (investimento, caixa)
          passa a enxergar o movimento no fluxo.
        </Text>
        <Flex gap={8} wrap="wrap" align="flex-end">
          <div style={{ minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Sai de</Text>
            <Select style={{ width: '100%' }} size="small" value={form.origem_conta_id || undefined}
              placeholder="conta de origem" options={contasOpts} showSearch optionFilterProp="label"
              onChange={v => setForm(f => ({ ...f, origem_conta_id: v }))} />
          </div>
          <div style={{ minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Entra em</Text>
            <Select style={{ width: '100%' }} size="small" value={form.destino_conta_id || undefined}
              placeholder="conta de destino" options={contasOpts} showSearch optionFilterProp="label"
              onChange={v => setForm(f => ({ ...f, destino_conta_id: v }))} />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Valor</Text>
            <InputNumber size="small" style={{ width: 130, display: 'block' }} min={0.01}
              decimalSeparator="," value={form.valor ?? undefined}
              onChange={v => setForm(f => ({ ...f, valor: v as number }))} />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Data</Text>
            <Input size="small" type="date" style={{ width: 140 }} value={form.data}
              onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
          </div>
          <div style={{ minWidth: 180, flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Descrição (opcional)</Text>
            <Input size="small" value={form.descricao} placeholder="Aplicação CDB…"
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} />
          </div>
          <Button size="small" type="primary" icon={<PlusOutlined />} loading={salvando}
            onClick={() => lancar(false)}>Lançar</Button>
        </Flex>
      </Card>

      <Table<Transf>
        size="small" rowKey="id" loading={carregando} dataSource={lista}
        pagination={{ pageSize: 10, size: 'small' }}
        columns={[
          { title: 'Data', dataIndex: 'data', width: 96, render: v => fmtDataBr(v) },
          {
            title: 'Transferência', ellipsis: true,
            render: (_, r) => (
              <span title={r.descricao || ''}>
                <Text style={{ fontSize: 12 }}>{r.origem_conta_nome || '?'} → {r.destino_conta_nome || '?'}</Text>
              </span>
            ),
          },
          {
            title: 'Valor', dataIndex: 'valor', width: 110, align: 'right',
            render: v => <Text strong style={{ fontSize: 12 }}>{fmt(Math.abs(parseFloat(v as any) || 0))}</Text>,
          },
          {
            title: 'Origem', dataIndex: 'origem', width: 110,
            render: v => <Tag style={{ fontSize: 10, margin: 0 }}>{ORIGEM_LABEL[v] || v}</Tag>,
          },
          {
            // O estado vem dos vínculos reais, lado a lado — é o que diz "o
            // extrato já confirmou este ato?"
            title: 'Conciliação', width: 170,
            render: (_, r) => (
              <Space size={4}>
                <Tag color={r.saida_conciliada ? 'green' : 'default'} style={{ fontSize: 10, margin: 0 }}>
                  saída {r.saida_conciliada ? '✓' : '—'}
                </Tag>
                <Tag color={r.entrada_conciliada ? 'green' : 'default'} style={{ fontSize: 10, margin: 0 }}>
                  entrada {r.entrada_conciliada ? '✓' : '—'}
                </Tag>
              </Space>
            ),
          },
          {
            title: '', width: 46,
            render: (_, r) => r.origem === 'manual' && !r.saida_conciliada && !r.entrada_conciliada ? (
              <Popconfirm title="Excluir esta transferência?" onConfirm={() => excluir(r.id)}>
                <Button size="small" danger type="text" icon={<DeleteOutlined />} />
              </Popconfirm>
            ) : null,
          },
        ]}
        locale={{ emptyText: 'Nenhuma transferência no período.' }}
      />
    </Modal>
  );
}
